import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { PayeeButtonComponent } from '../../payee-buttons/payee-button/payee-button.component';
import { PayeeAutocompleteComponent } from '../../payee-form-fields/payee-autocomplete/payee-autocomplete.component';
import { PayeeFormFieldComponent } from '../../payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeePopupComponent } from '../../payee-popup/payee-popup.component';
import {
  RunTransition,
  RunTransitionWithDialog,
  WorkflowService,
} from '../../services/workflow/workflow.service';

type CompanyOption = {
  name: string;
  address: string | null;
  easyEstablishment: string;
};

type PartyData = {
  name: string | null;
  selected_easy_establishment: string | null;
  companies: CompanyOption[];
};

type DialogData = {
  transition_label: string;
  debtor: PartyData;
  client: PartyData;
};

@Component({
  selector: 'app-create-coface-case-dialog',
  templateUrl: './create-coface-case-dialog.component.html',
  styleUrls: ['./create-coface-case-dialog.component.scss'],
  imports: [
    FormsModule,
    MatProgressSpinner,
    PayeeAutocompleteComponent,
    PayeeButtonComponent,
    PayeeFormFieldComponent,
    PayeePopupComponent,
    ReactiveFormsModule,
    TranslateModule,
  ],
})
export class CreateCofaceCaseDialogComponent implements OnInit {
  private readonly dialogRef =
    inject<MatDialogRef<CreateCofaceCaseDialogComponent, RunTransition | undefined>>(MatDialogRef);
  readonly data = inject<RunTransitionWithDialog>(MAT_DIALOG_DATA);
  private readonly workflowService = inject(WorkflowService);
  private readonly fb = inject(FormBuilder);

  readonly dataLoading = signal(false);
  loading = false;
  dialogData?: DialogData;

  readonly form = this.fb.group({
    debtor_company: this.fb.control<CompanyOption | null>(null),
    client_company: this.fb.control<CompanyOption | null>(null),
  });

  get debtorCompany() {
    return this.form.controls.debtor_company;
  }

  get clientCompany() {
    return this.form.controls.client_company;
  }

  async ngOnInit() {
    this.dataLoading.set(true);
    try {
      this.dialogData = (await this.workflowService.getDialogData({
        caseId: this.data.caseId,
        transitionId: this.data.transitionId,
      })) as DialogData;

      this.debtorCompany.patchValue(this.preselectedCompany(this.dialogData.debtor));
      this.clientCompany.patchValue(this.preselectedCompany(this.dialogData.client));
    } finally {
      this.dataLoading.set(false);
    }
  }

  displayCompany = (company: CompanyOption | null): string => {
    if (!company) {
      return '';
    }

    return company.address
      ? `${company.name} — ${company.address}`
      : company.name;
  };

  readonly cancel = () => {
    this.dialogRef.close();
  };

  readonly submit = async () => {
    this.form.markAllAsTouched();

    if (!this.dialogData || this.form.invalid) {
      return;
    }

    const { debtor_company, client_company } = this.form.getRawValue();

    const params: RunTransition['params'] = {
      ...(this.data.extraParams ?? {}),
    };

    if (debtor_company) {
      params.debtor_easy_establishment = debtor_company.easyEstablishment;
    }

    if (client_company) {
      params.client_easy_establishment = client_company.easyEstablishment;
    }

    const closeValue: RunTransition = {
      caseId: this.data.caseId,
      transitionId: this.data.transitionId,
      params,
    };

    this.loading = true;
    try {
      await this.workflowService.runTransition(closeValue);
      this.dialogRef.close(closeValue);
    } catch (error) {
      this.workflowService.showTransitionError(error);
    } finally {
      this.loading = false;
    }
  };

  private preselectedCompany(party: PartyData): CompanyOption | null {
    const selected = party.companies.find(
      (company) => company.easyEstablishment === party.selected_easy_establishment,
    );
    if (selected) {
      return selected;
    }

    return party.companies.length === 1 ? party.companies[0] : null;
  }
}
